import {
  GET_SCAN_BY_ID,
  GET_SCAN_SUMMARY_FOR_PROJECT,
} from "../graphql/scan-queries";
import {
  LUXCLOUD_PDAL,
  LUXCLOUD_PUBLISH_SCANS,
} from "@/graphql/lux-cloud-queries";

import { GET_PROJECT } from "@/graphql/project-queries";
import { GET_PROJECT_LAYERS_FOR_PROJECT } from "@/graphql/project-layer-queries";
import { GET_ANNOTATIONS } from "@/graphql/annotation-queries";
import { ScanSummary } from "./../models/models";
import { apolloClient } from "../services/vue-apollo";
import { getSignedUrl } from "../services/utilities";
import { bootstrap } from "../utils/vuex-bootstrap";
import moment from "moment";

/**
 * True for browser-level request cancellations (fetch AbortError), which show
 * up when duplicate dispatches or navigation cancel an in-flight query. These
 * are not server failures — they are safe to retry once.
 */
const isAbortError = (error: any): boolean => {
  const network = error?.networkError;
  return (
    network?.name === "AbortError" ||
    /abort/i.test(network?.message || "") ||
    error?.name === "AbortError" ||
    /abort/i.test(error?.message || "")
  );
};

/** Run an Apollo query, retrying exactly once if the request was aborted. */
const queryWithAbortRetry = (options: any): Promise<any> =>
  apolloClient.query(options).catch((error: any) => {
    if (isAbortError(error)) {
      return apolloClient.query(options);
    }
    throw error;
  });

// Project id whose setProjectId load is currently in flight (module-local, not
// reactive). Used to drop duplicate dispatches for the same project — those
// previously raced/aborted each other's requests and left an empty scan list.
let inflightProjectId: number | null = null;

const viewerStoreModule = bootstrap({
  namespaced: true,
  state: {
    projectId: 0,
    project: null,

    // the summary lists used by screens
    summaryScanList: [] as ScanSummary[],
    projectLayerList: [],

    // tracks the collection of fully loaded scans
    loadedScanList: [],

    // state tracking lists
    selectedProjectLayerIdList: [],
    visibleScanIdList: [],
    filteredScanIdList: [],
    selectedScanIdList: [],
    selectedAnnotationsList: [],

    // focus related
    focusedScanId: null,
    focusedPoint: null,
    focusedImageSet: null,
    focusedVersion: null,

    // state related (for image-view)
    imageViewVisible: false,

    // elevation profile related
    elevationProfilePoints: [],
    elevationProfileSource: null, // Source point coordinates
    elevationProfileDestination: null, // Destination point coordinates
    elevationProfileVisible: false,

    // event related (for point-cloud-view)
    loadedScan: null,
    loadedManyScans: null,
    //autofocus
    autoFocus: true,
    // filter related
    filterSelected: false,

    //annotation related
    focusToAnnotation: false,
    focusedAnnotation: null,
    filterAnnotations: false,
    annotations: [],

    boundingBoxes: {},
    removeClip: null,
  },
  mutations: {
    setProjectId(state, projectId) {
      state.projectId = parseInt(projectId);
    },
    setAnnotations(state, annotations) {
      if (!Array.isArray(annotations)) {
        annotations = [];
      }
      state.annotations = [...annotations].sort((b, a) =>
        a.id.localeCompare(b.id)
      );
    },
    setBoundingBoxes(state, boundingBoxes) {
      state.boundingBoxes = boundingBoxes;
    },
    setRemoveClip(state, removeClip) {
      state.removeClip = removeClip;
    },
    setImageViewVisible(state, value) {
      state.imageViewVisible = value;
    },

    setElevationProfilePoints(state, points) {
      state.elevationProfilePoints = points;
    },

    setElevationProfileSource(state, source) {
      state.elevationProfileSource = source;
    },

    setElevationProfileDestination(state, destination) {
      state.elevationProfileDestination = destination;
    },

    setElevationProfileVisible(state, value) {
      state.elevationProfileVisible = value;
    },

    clearElevationProfile(state) {
      state.elevationProfilePoints = [];
      state.elevationProfileSource = null;
      state.elevationProfileDestination = null;
    },

    setSummaryScanList(state, summaryScanList) {
      state.summaryScanList = [...summaryScanList];
    },

    setProject(state, project) {
      state.project = project;
    },

    setProjectLayers(state, projectLayerList) {
      state.projectLayerList = projectLayerList;
    },

    addLoadedScan(state, scan) {
      state.loadedScan = scan.id;
      state.loadedScanList.push(scan);
    },

    mergeLoadedScan(state, scanList) {
      state.loadedScanList = state.loadedScanList.concat(scanList);
    },

    setFilteredScanIdList(state, filteredScanIdList) {
      state.filteredScanIdList = [...filteredScanIdList];
    },

    setFilterSelected(state, value) {
      state.filterSelected = value;
    },
    setFocusToAnnotation(state, value) {
      state.focusToAnnotation = value;
    },
    setFocusedAnnotation(state, value) {
      state.focusedAnnotation = value;
    },
    setFilterAnnotations(state, value) {
      state.filterAnnotations = value;
    },
    setAutoFocus(state, value) {
      state.autoFocus = value;
    },
    addSelectedScanIdList(state, scanId) {
      if (!state.selectedScanIdList.some((x) => x === scanId)) {
        state.selectedScanIdList.push(scanId);
      }
    },
    removeSelectedScanIdList(state, scanId) {
      if (state.selectedScanIdList.some((x) => x === scanId)) {
        state.selectedScanIdList = state.selectedScanIdList.filter(
          (x) => x !== scanId
        );
      }
    },
    addSelectedAnnotationsList(state, annotation) {
      if (!state.selectedAnnotationsList.some((x) => x === annotation)) {
        state.selectedAnnotationsList.push(annotation);
      }
    },
    removeSelectedAnnotationsList(state, annotation) {
      if (state.selectedAnnotationsList.some((x) => x === annotation)) {
        state.selectedAnnotationsList = state.selectedAnnotationsList.filter(
          (x) => x !== annotation
        );
      }
    },
    addManySelectedScanIdList(state, scanIdList) {
      state.selectedScanIdList = scanIdList;
    },

    addProjectLayerIdList(state, projectLayerId) {
      if (!state.selectedProjectLayerIdList.some((x) => x === projectLayerId)) {
        state.selectedProjectLayerIdList.push(projectLayerId);
      }
    },

    removeProjectLayerIdList(state, projectLayerId) {
      if (state.selectedProjectLayerIdList.some((x) => x === projectLayerId)) {
        state.selectedProjectLayerIdList =
          state.selectedProjectLayerIdList.filter((x) => x !== projectLayerId);
      }
    },

    addVisibleScanIdList(state, scanId) {
      if (!state.visibleScanIdList.some((x) => x === scanId)) {
        state.visibleScanIdList.push(scanId);
      }
    },

    removeVisibleScanIdList(state, scanId) {
      if (state.visibleScanIdList.some((x) => x === scanId)) {
        state.visibleScanIdList = state.visibleScanIdList.filter(
          (x) => x !== scanId
        );
      }
    },
    setFocusedVersion(state, value) {
      state.focusedVersion = value;
    },
    setFocusedScanId(state, value) {
      state.focusedScanId = value;
    },
    setFocusedPoint(state, value) {
      state.focusedPoint = value;
    },
    setFocusedImageSet(state, value) {
      state.focusedImageSet = value;
    },
    // Cache an on-demand-signed image URL onto the set's camera image so the
    // <v-img> renders and re-focusing the same set doesn't re-sign.
    setImageSetUrl(state, { set, slot, url }) {
      if (set && set[slot]) {
        set[slot].s3Url = url;
      }
    },

    resetFocus(state) {
      state.focusedImageSet = null;
      state.focusedPoint = null;
      state.focusedScanId = null;
      state.imageViewVisible = false;
    },

    resetsSelectedScanIdList(state) {
      state.selectedScanIdList = [];
    },
    resetSelectedAnnotationsList(state) {
      state.selectedAnnotationsList = [];
    },
    resetLoadedScanList(state) {
      state.loadedScanList = [];
    },
    resetSelectedProjectLayerIdList(state) {
      state.selectedprojectLayerIdList = [];
    },
    resetVisibleScanIdList(state) {
      state.visibleScanIdList = [];
    },
    resetFilteredScanIdList(state) {
      state.filteredScanIdList = [];
    },
  },
  actions: {
    // TODO: consider moving functions from the store as they do not change state
    publishSelected(
      { commit, state },
      publishScans: {
        bucketId: number;
        targetFolder: string;
        includeImages: boolean;
        transform: boolean;
      }
    ) {
      let scans;
      if (state.focusedScanId) {
        scans = {
          scan: state.focusedScanId,
          session: state.summaryScanList.find(
            (x) => x.id === state.focusedScanId
          )?.sessionId,
        };
      } else {
        scans = state.selectedScanIdList.map((x) => {
          return {
            scan: x,
            session: state.summaryScanList.find((y) => y.id == x).sessionId,
          };
        });
      }

      const variables = {
        publishScan: {
          projectId: state.projectId,
          bucketId: publishScans.bucketId,
          targetFolder: publishScans.targetFolder,
          includeImages: publishScans.includeImages,
          transform: publishScans.transform,
          scans: scans,
        },
      };
      apolloClient
        .mutate({
          mutation: LUXCLOUD_PUBLISH_SCANS,
          variables: variables,
        })
        .then(() => {
          // TODO: confirm this works, otherwise use code below
          commit(
            "showMessage",
            {
              content: `Successfully submitted selected scans for publishing`,
              color: "green",
            },
            { root: true }
          );
        })
        .catch((error) => {
          commit(
            "showMessage",
            { content: error.message, color: "error" },
            { root: true }
          );
        });
    },
    processPDALSelected(
      { commit, state },
      pdalSubmission: { versionName: string; pipeline; version }
    ) {
      let artifactIds = [];
      if (state.focusedScanId) {
        const scan = state.summaryScanList.find(
          (x) => x.id === state.focusedScanId
        );
        artifactIds.push(
          scan.artifacts.find(
            (x) =>
              x.name === pdalSubmission.version &&
              x.artifactType === "pointcloud"
          )?.id
        );
      } else {
        for (const scanId of state.selectedScanIdList) {
          const scan = state.summaryScanList.find((x) => x.id === scanId);
          artifactIds.push(
            scan.artifacts.find(
              (x) =>
                x.name === "unclassified" && x.artifactType === "pointcloud"
            )?.id
          );
        }
      }
      // change them all to ints
      artifactIds = artifactIds.map((x) => parseInt(x));
      const pipelinejson = pdalSubmission.pipeline;
      const variables = {
        submission: {
          artifactIds: artifactIds,
          name: pdalSubmission.versionName,
          pipeline: JSON.stringify(pipelinejson),
        },
      };
      apolloClient
        .mutate({
          mutation: LUXCLOUD_PDAL,
          variables: variables,
        })
        .then(() => {
          commit(
            "showMessage",
            {
              content: `Successfully submitted selected scans for processing`,
              color: "green",
            },
            { root: true }
          );
        })
        .catch((error) => {
          commit(
            "showMessage",
            { content: error.message, color: "error" },
            { root: true }
          );
        });
    },
    setFilteredScanIdList({ commit }, filteredScanIdList) {
      commit("setFilteredScanIdList", filteredScanIdList);
    },
    setFilterSelected({ commit }, value) {
      commit("setFilterSelected", value);
    },
    setFocusToAnnotation({ commit }, value) {
      commit("setFocusToAnnotation", value);
    },
    setFocusedAnnotation({ commit }, value) {
      commit("setFocusedAnnotation", value);
    },
    setFilterAnnotations({ commit }, value) {
      commit("setFilterAnnotations", value);
    },
    setAutoFocus({ commit }, value) {
      commit("setAutoFocus", value);
    },
    // scan selection actions
    selectScan({ commit, dispatch, state }, scanId) {
      // Check if scan actually exists in the summary list before selecting
      const scanExists = state.summaryScanList.some(scan => scan.id === scanId);
      
      if (!scanExists) {
        console.warn(`Scan ${scanId} not found in summary list, cannot select`);
        commit("showMessage", {
          content: `Scan ${scanId} not found in the current project`,
          color: "warning",
          timeout: 5000
        }, { root: true });
        return;
      }
      
      // ✅ FIX: Add additional validation to prevent loading corrupted scans
      const scan = state.summaryScanList.find(scan => scan.id === scanId);
      if (scan && (!scan.id)) {
        console.warn(`Scan ${scanId} has corrupted or missing data, cannot select`);
        commit("showMessage", {
          content: `This scan file has incomplete data. Try using the "Zoom to view all selected scans" button instead.`,
          color: "info",
          timeout: 8000
        }, { root: true });
        return;
      }
      
      // For imported scans, sessionId is not required
      if (scan && scan.assignmentType !== "Imported" && !scan.sessionId) {
        console.warn(`Scan ${scanId} is missing session ID, cannot select`);
        commit("showMessage", {
          content: `This scan file has incomplete data. Try using the "Zoom to view all selected scans" button instead.`,
          color: "info",
          timeout: 8000
        }, { root: true });
        return;
      }
      
      commit("setRemoveClip", scanId);
      commit("addSelectedScanIdList", scanId);
      commit("addVisibleScanIdList", scanId);
      dispatch("loadScan", scanId);
    },
    unselectScan({ commit, state }, scanId) {
      commit("setRemoveClip", null);
      commit("removeVisibleScanIdList", scanId);
      commit("removeSelectedScanIdList", scanId);
      if (state.focusedScanId === scanId) commit("resetFocus");
    },
    selectAnnotation({ commit, dispatch }, annotation) {
      commit("addSelectedAnnotationsList", annotation);
      
      // Also select and show the corresponding scan
      const scanId = annotation.scan.id;
      if (scanId) {
        dispatch("selectScan", scanId);
      }
    },
    unselectAnnotation({ commit, state }, annotation) {
      commit("removeSelectedAnnotationsList", annotation);
      
      // Check if there are any other selected annotations for this scan
      const scanId = annotation.scan.id;
      const hasOtherAnnotationsForScan = state.selectedAnnotationsList.some(
        selectedAnnotation => selectedAnnotation.scan.id === scanId && selectedAnnotation.id !== annotation.id
      );
      
      // If no other annotations are selected for this scan, unselect the scan
      if (!hasOtherAnnotationsForScan && scanId) {
        commit("removeSelectedScanIdList", scanId);
        commit("removeVisibleScanIdList", scanId);
      }
    },
    selectManyScans({ commit, dispatch, state }, selectedScanIdList) {
      commit("addManySelectedScanIdList", selectedScanIdList);
      selectedScanIdList.forEach((scanId) => {
        if (!state.visibleScanIdList.includes(scanId)) {
          commit("addVisibleScanIdList", scanId);
        }
      });
      dispatch("loadManyScans", selectedScanIdList);
    },
    unselectAllScans({ commit }) {
      commit("resetsSelectedScanIdList");
      commit("resetFocus");
    },

    // scan visibility actions (requires selected)
    showScan({ commit }, scanId) {
      commit("addVisibleScanIdList", scanId);
    },
    hideScan({ commit, state }, scanId) {
      commit("removeVisibleScanIdList", scanId);
      if (state.focusedScanId === scanId) commit("resetFocus");
    },

    // related to showing/hiding the image viewer
    showImageView({ commit }) {
      commit("setImageViewVisible", true);
    },
    hideImageView({ commit }) {
      commit("setImageViewVisible", false);
    },
    makeDefaultVersion({ commit }, selectedVersion) {
      commit("setDefaultVersion", selectedVersion);
    },
    // scan focus actions (requires selected and visible)
    focusScanId({ commit, state }, scanId) {
      commit("setRemoveClip", null);
      commit("setRemoveClip", scanId);
      commit("setFocusedScanId", scanId);
      const scan = state.loadedScanList.find((x) => x.id === scanId);
      if (scan && scan.images.length > 0) {
        commit("setFocusedImageSet", scan.images[0]);
      } else {
        commit("setFocusedImageSet", null);
      }
    },
    focusVersion({ commit }, selectedVersion) {
      commit("setFocusedVersion", selectedVersion);
    },
    focusPoint({ commit }, point) {
      commit("setFocusedPoint", point);
    },
    focusImageSet({ commit }, image) {
      commit("setFocusedImageSet", image);
    },
    // Sign the (up to 3) camera-image URLs for the focused image set on demand.
    // The scan-load query deliberately omits per-frame presigned URLs — signing
    // every frame (thousands x 3 cameras, ~10MB) made the query abort in the
    // browser — so we sign only the frame actually being viewed, when it becomes
    // focused. The backend derives the bucket + tenancy from the bare key.
    async signImageSetUrls({ commit, state }, imageSet) {
      const set = imageSet || state.focusedImageSet;
      if (!set) return;
      const scan = state.loadedScanList.find((x) => x.id === state.focusedScanId);
      if (!scan || !scan.device || !scan.session) return;
      const base = `${scan.device.id}/processed/${scan.session.id}/${scan.id}`;
      const slots = ["leftImage", "centerImage", "rightImage"];
      await Promise.all(
        slots.map(async (slot) => {
          const img = set[slot];
          if (!img || !img.filename || !img.camera || img.s3Url) return;
          try {
            const key = `${base}/${img.camera}_cam/undistorted/${img.filename}`;
            const url = await getSignedUrl(key);
            if (url) commit("setImageSetUrl", { set, slot, url });
          } catch (e) {
            console.warn(`Failed to sign image URL (${slot}):`, e);
          }
        })
      );
    },
    setBoundingBoxes({ commit }, boundingBoxes) {
      commit("setBoundingBoxes", boundingBoxes);
    },
    // Load annotations from server
    async loadAnnotations({ commit }) {
      try {
        const response = await apolloClient.query({
          query: GET_ANNOTATIONS,
          fetchPolicy: "network-only",
        });
        
        const annotations = response.data?.annotations || [];
        commit("setAnnotations", annotations);
        
        return annotations;
      } catch (error) {
        console.error('❌ Failed to load annotations from server:', error);
        
        // Set empty array on error to prevent crashes
        commit("setAnnotations", []);
        
        // Don't show error message for authentication issues to avoid spam
        if (!error.message?.includes('token') && !error.message?.includes('unauthorized')) {
          commit("showMessage", {
            content: "Failed to load annotations. Some annotations may not be visible.",
            color: "warning",
            timeout: 5000
          }, { root: true });
        }
        
        return [];
      }
    },
    // related to setting a project
    setProjectId({ commit, dispatch, state }, rawProjectId) {
      // The backend exposes Project.id as GraphQL ID, which SERIALIZES AS A
      // STRING — but the project queries take $projectId: Float!. Passing the
      // string straight through fails variable coercion with HTTP 400
      // ("Float cannot represent non numeric value"), so normalize to a
      // number here, at the single entry point every caller goes through.
      const projectId =
        typeof rawProjectId === "string" ? Number(rawProjectId) : rawProjectId;
      if (projectId === null || projectId === undefined || Number.isNaN(projectId)) {
        console.error("setProjectId: invalid project id", rawProjectId);
        return;
      }
      // Drop duplicate dispatches while the SAME project is still loading —
      // they previously cancelled each other's requests (AbortError) and left
      // the scans list empty with no feedback.
      if (inflightProjectId === projectId) {
        return;
      }
      inflightProjectId = projectId;
      commit("setProjectId", projectId);

      const pendingQueries = projectId !== 0 ? 3 : 1;
      let settledQueries = 0;
      const settle = () => {
        settledQueries += 1;
        if (settledQueries >= pendingQueries && inflightProjectId === projectId) {
          inflightProjectId = null;
        }
      };

      // Surface failures instead of silently rendering nothing. Aborted
      // requests are retried once (queryWithAbortRetry); a second abort means
      // the page is navigating away, so it is logged but not toasted.
      const reportFailure = (what: string, error: any) => {
        console.error(`Failed to load ${what}:`, error);
        if (isAbortError(error)) {
          return;
        }
        commit(
          "showMessage",
          {
            content: `Failed to load ${what} for the selected project. Please try selecting the project again.`,
            color: "error",
            timeout: 6000,
          },
          { root: true }
        );
      };

      commit("startLoading", null, { root: true });
      queryWithAbortRetry({
        query: GET_SCAN_SUMMARY_FOR_PROJECT,
        variables: { projectId },
        fetchPolicy: "network-only",
      })
        .then((res) => {
          if (state.projectId !== projectId) {
            return; // a newer project was selected — drop the stale response
          }
          const scansSummary = res.data.scansSummary;
          commit("setSummaryScanList", scansSummary);
          dispatch("resetState");
        })
        .catch((error) => reportFailure("scans", error))
        .finally(() => {
          commit("stopLoading", null, { root: true });
          settle();
        });

      if (projectId !== 0) {
        // projectId of 0 represents unassigned scans, so no project by definition
        commit("startLoading", null, { root: true });
        queryWithAbortRetry({
          query: GET_PROJECT,
          variables: { projectId },
        })
          .then((res) => {
            if (state.projectId !== projectId) {
              return;
            }
            const project = res.data.project;
            commit("setProject", project);
          })
          .catch((error) => reportFailure("project details", error))
          .finally(() => {
            commit("stopLoading", null, { root: true });
            settle();
          });

        commit("startLoading", null, { root: true });
        queryWithAbortRetry({
          query: GET_PROJECT_LAYERS_FOR_PROJECT,
          variables: { projectId },
        })
          .then((res) => {
            if (state.projectId !== projectId) {
              return;
            }
            commit(
              "setProjectLayers",
              res.data.projectLayers
                ?.filter((x) => !x.ignore)
                .sort((a, b) => a.name.localeCompare(b.name))
            );
            // preselect layers which are showByDefaul
            for (const layer of res.data.projectLayers) {
              if (layer.showByDefault)
                commit("addProjectLayerIdList", layer.id);
            }
          })
          .catch((error) => reportFailure("project layers", error))
          .finally(() => {
            commit("stopLoading", null, { root: true });
            settle();
          });
      }
    },
    resetSelectedAnnotationsList({ commit }) {
      commit("resetSelectedAnnotationsList");
    },
    resetState({ commit }) {
      commit("resetFocus");
      commit("resetSelectedProjectLayerIdList");
      commit("resetVisibleScanIdList");
      commit("resetLoadedScanList");
      commit("resetFilteredScanIdList");
      commit("resetsSelectedScanIdList");
      commit("resetSelectedAnnotationsList");
    },

    // related to loading scan details
    loadScan({ commit, dispatch, state }, scanId) {
      const toLoad = state.selectedScanIdList.filter(
        (selected) =>
          !state.loadedScanList.find((loaded) => loaded.id === selected)
      );
      if (toLoad.length > 0) {
        commit("startLoading", null, { root: true });
        apolloClient
          .query({
            query: GET_SCAN_BY_ID,
            variables: { id: scanId },
            errorPolicy: 'all',
            fetchPolicy: 'no-cache'
          })
          .then((res) => {
            const scan = res.data?.scan;
            
            // Check if scan data is valid
            if (scan && scan.id) {
              commit("addLoadedScan", scan);
              // only focus the scan if it is the first one selected
              if (!state.focusedScanId) {
                if (state.autoFocus) {
                  dispatch("focusScanId", scan.id);
                }
              }
            } else {
              console.warn(`❌ Scan ${scanId} not found in response:`, res);
              // ✅ FIX: Remove the invalid scan from selection
              commit("removeSelectedScanIdList", scanId);
              commit("removeVisibleScanIdList", scanId);
              if (state.focusedScanId === scanId) {
                commit("resetFocus");
              }
              
              commit("showMessage", {
                content: `Scan ${scanId} could not be loaded. It may be temporarily unavailable.`,
                color: "info",
                timeout: 5000
              }, { root: true });
            }
          })
          .catch((error) => {
            console.warn(`❌ Failed to load scan ${scanId}:`, error);
            console.warn(`Error details:`, {
              message: error.message,
              graphQLErrors: error.graphQLErrors,
              networkError: error.networkError,
              extraInfo: error.extraInfo
            });
            
            // ✅ FIX: Remove the failed scan from selection
            commit("removeSelectedScanIdList", scanId);
            commit("removeVisibleScanIdList", scanId);
            if (state.focusedScanId === scanId) {
              commit("resetFocus");
            }
            
            if (error.message && (
              error.message.includes('token') || 
              error.message.includes('expired') ||
              error.message.includes('unauthorized') ||
              error.message.includes('authentication')
            )) {
              commit("showMessage", {
                content: "Your session has expired. Please refresh the page and log in again.",
                color: "error",
                timeout: 8000
              }, { root: true });
            } else {
              commit("showMessage", {
                content: `Scan ${scanId} could not be loaded. Please try again later.`,
                color: "info",
                timeout: 5000
              }, { root: true });
            }
          })
          .finally(() => {
            commit("stopLoading", null, { root: true });
          });
      }
    },
    
    async loadManyScans({ commit, state }, selectedScanIdList) {
      commit("startLoading", null, { root: true });
      const toLoad = selectedScanIdList.filter(
        (selected) =>
          !state.loadedScanList.find((loaded) => loaded.id === selected)
      );
      
      const promises = [];
      let authError = false;
      
      for (const scanId of toLoad) {
        promises.push(
          apolloClient
            .query({
              query: GET_SCAN_BY_ID,
              variables: { id: scanId },
              errorPolicy: 'all',
              fetchPolicy: 'no-cache'
            })
            .then((res) => {
              const scan = res.data?.scan;
              if (scan && scan.id) {
                return scan;
              } else {
                console.warn(`Scan ${scanId} not found or invalid, removing from selection`);
                commit("removeSelectedScanIdList", scanId);
                commit("removeVisibleScanIdList", scanId);
                if (state.focusedScanId === scanId) {
                  commit("resetFocus");
                }
                return null;
              }
            })
            .catch((error) => {
              console.warn(`Failed to load scan ${scanId}:`, error);
              
              // Check for authentication errors
              if (error.message && (
                error.message.includes('token') || 
                error.message.includes('expired') ||
                error.message.includes('unauthorized') ||
                error.message.includes('authentication')
              )) {
                authError = true;
              }
              
              // Remove the failed scan from selection
              commit("removeSelectedScanIdList", scanId);
              commit("removeVisibleScanIdList", scanId);
              if (state.focusedScanId === scanId) {
                commit("resetFocus");
              }
              
              return null;
            })
        );
      }
      
      try {
        const loadedScans = await Promise.all(promises);
        // Filter out null values (failed scans)
        const validScans = loadedScans.filter(scan => scan !== null);
        
        if (validScans.length > 0) {
          commit("mergeLoadedScan", validScans);
        }
        
        // Show appropriate error messages
        const failedCount = loadedScans.length - validScans.length;
        if (authError) {
          commit("showMessage", {
            content: "Your session has expired. Please refresh the page and log in again.",
            color: "error",
            timeout: 8000
          }, { root: true });
        } else if (failedCount > 0) {
          commit("showMessage", {
            content: `${failedCount} scan(s) were removed because they no longer exist or you don't have access to them.`,
            color: "info",
            timeout: 5000
          }, { root: true });
        }
        
      } catch (error) {
        console.warn("Error in loadManyScans:", error);
        commit("showMessage", {
          content: "Some scans failed to load and were removed from selection.",
          color: "warning",
          timeout: 5000
        }, { root: true });
      }
      
      commit("stopLoading", null, { root: true });
    },

    removeInvalidScan({ commit, state }, scanId) {
      commit("removeSelectedScanIdList", scanId);
      commit("removeVisibleScanIdList", scanId);
      if (state.focusedScanId === scanId) {
        commit("resetFocus");
      }
    },

    // related to project layers
    selectProjectLayer({ commit }, projectLayerId) {
      commit("addProjectLayerIdList", projectLayerId);
    },
    unselectProjectLayer({ commit }, projectLayerId) {
      commit("removeProjectLayerIdList", projectLayerId);
    },

    // elevation profile actions
    setElevationProfilePoints({ commit }, points) {
      commit("setElevationProfilePoints", points);
    },

    setElevationProfileSource({ commit }, source) {
      commit("setElevationProfileSource", source);
    },

    setElevationProfileDestination({ commit }, destination) {
      commit("setElevationProfileDestination", destination);
    },

    setElevationProfileVisible({ commit }, value) {
      commit("setElevationProfileVisible", value);
    },

    clearElevationProfile({ commit }) {
      commit("clearElevationProfile");
    },
  },
  getters: {
    // access getters
    annotations: (state) => state.annotations,
    boundingBoxes: (state) => state.boundingBoxes,
    removeClip: (state) => state.removeClip,
    projectId: (state) => state.projectId,
    autoFocus: (state) => state.autoFocus,
    filterSelected: (state) => state.filterSelected,
    focusToAnnotation: (state) => state.focusToAnnotation,
    focusedAnnotation: (state) => state.focusedAnnotation,
    filterAnnotations: (state) => state.filterAnnotations,
    project: (state) => state.project,
    summaryScanList: (state) => state.summaryScanList,
    projectLayerList: (state) => state.projectLayerList,
    selectedProjectLayerIdList: (state) => state.selectedProjectLayerIdList,
    selectedScanIdList: (state) => state.selectedScanIdList,
    selectedAnnotationsList: (state) => state.selectedAnnotationsList,
    loadedScanList: (state) => state.loadedScanList,
    filteredScanIdList: (state) => state.filteredScanIdList,
    visibleScanIdList: (state) => state.visibleScanIdList,
    focusedVersion: (state) => state.focusedVersion,
    focusedScanId: (state) => state.focusedScanId,
    focusedImageSet: (state) => state.focusedImageSet,
    focusedPoint: (state) => state.focusedPoint,
    loadedScan: (state) => state.loadedScan,
    imageViewVisible: (state) => state.imageViewVisible,
    elevationProfilePoints: (state) => state.elevationProfilePoints,
    elevationProfileSource: (state) => state.elevationProfileSource,
    elevationProfileDestination: (state) => state.elevationProfileDestination,
    elevationProfileVisible: (state) => state.elevationProfileVisible,

    defaultScanTags: () => {
      return ["QA - OK", "QA - Issue/Poor"];
    },

    defaultPipelineTags: () => {
      return ["3D Bounding Box"];
    },
    // computed getters
    filteredAnnotationsList: (state) => {
      let annotationsList = [];
      for (const scan of state.summaryScanList) {
        for (const annotation of state.annotations) {
          if (annotation.scan.id === scan.id) {
            annotationsList.push(scan.id);
          }
        }
      }
      annotationsList = [...new Set(annotationsList)];
      return annotationsList;
    },
    annotationsByProjectList: (state) => {
      let annotationsList = [];
      for (const scan of state.summaryScanList) {
        for (const annotation of state.annotations) {
          if (annotation.scan.id === scan.id) {
            annotationsList.push(annotation);
          }
        }
      }
      annotationsList = [...new Set(annotationsList)];
      return annotationsList;
    },
    someScanSelected: (state) => state.selectedScanIdList.length > 0,
    totalScans: (state) => state.summaryScanList.length,
    filteredSummaryScanList: (state) => {
      if (state.filteredScanIdList.length === 0) return state.summaryScanList;
      return state.summaryScanList.filter((scan) =>
        state.filteredScanIdList.find((filter) => filter === scan.id)
      );
    },
    focusedScan: (state) =>
      state.loadedScanList.find((x) => x.id === state.focusedScanId),
    isFocused: (state) => state.focusedScanId !== null,

    // method getters
    availableDevices(state): string[] {
      const unique = [
        ...new Set<string>(
          state.summaryScanList
            ?.filter((x) => x.deviceId !== null)
            .map((x) => x.deviceId)
        ),
      ];
      return ["(ALL)"].concat(unique);
    },
    availableTypes(state): string[] {
      const unique = [
        ...new Set<string>(
          state.summaryScanList
            ?.filter((x) => x.assignmentType !== null)
            .map((x) => x.assignmentType)
        ),
      ];
      return ["(ALL)"].concat(unique);
    },
    availableTags(state): string[] {
      const unique = [
        ...new Set<string>(state.summaryScanList.map((x) => x.tags).flat()),
      ];
      return ["(Empty)"].concat(unique);
    },
    isFiltered(state) {
      if (
        state.filteredScanIdList.length === 0 ||
        state.filteredScanIdList.length === state.summaryScanList.length
      )
        return false;
      return true;
    },
    availableDates(state): string[] {
      if (state.summaryScanList === null || state.summaryScanList.length === 0)
        return [];

      //const maxdate = new Date(state.summaryScanList.filter((x) => x.capturedTimestamp !== null).map(x => x.capturedTimestamp).reduce((a,b) => (a > b) ? a : b))
      //maxdate.setDate(maxdate.getDate() + 1);

      const results = state.summaryScanList
        ?.filter((x) => x.capturedTimestamp !== null)
        .map((x) => Date.parse(x.capturedTimestamp));

      //results.push(maxdate)

      return results
        .sort((a, b) => a - b)
        .map((x: Date) => moment(x).format("YYYY-MM-DD"))
        ?.filter(function (elem, index, self) {
          return index === self.indexOf(elem);
        });
    },
    isScanSelected: (state) => (scanId) => {
      return state.selectedScanIdList.some((x: string) => x === scanId);
    },
    isAnnotationSelected: (state) => (annotation) => {
      return state.selectedAnnotationsList.some((x) => x.id === annotation);
    },
    isScanLoaded: (state) => (scanId) => {
      return state.loadedScanList.some((x) => x.id === scanId);
    },
    isScanVisible: (state) => (scanId) => {
      return state.visibleScanIdList.some((x: string) => x === scanId);
    },
    isProjectLayerSelected: (state) => (projectLayerId) => {
      return state.selectedProjectLayerIdList.some(
        (x: string) => x === projectLayerId
      );
    },
    isProjectLayer: (state) => (projectLayerId) => {
      return state.projectLayerList.some((x) => x.id === projectLayerId);
    },
  },
});

export default viewerStoreModule;
